import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  chatbubbleEllipsesOutline,
  mailOutline,
  documentTextOutline,
  shieldCheckmarkOutline,
  informationCircleOutline,
  linkOutline,
} from 'ionicons/icons';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { categories, helpArticles } from '../../help/content';
import type { HelpArticle, CategoryInfo } from '../../help/content';
import ProCard from '../../components/pro/ProCard';
import ProListRow from '../../components/pro/ProListRow';
import './ProAccount.css';

const SUPPORT_EMAIL = 'support@capitolcommons.ai';
const TERMS_URL = 'https://capitolcommons.ai/terms';
const PRIVACY_URL = 'https://capitolcommons.ai/privacy';

/**
 * Groups the registered help articles by their category, preserving the
 * category `order` field, and drops any category that has no articles.
 */
const groupArticlesByCategory = (
  articles: HelpArticle[],
  cats: CategoryInfo[],
): Array<{ category: CategoryInfo; articles: HelpArticle[] }> =>
  [...cats]
    .sort((a, b) => a.order - b.order)
    .map((category) => ({
      category,
      articles: articles.filter((a) => a.category === category.id),
    }))
    .filter((group) => group.articles.length > 0);

const ProHelpPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('pro');

  const [nativeVersion, setNativeVersion] = useState<string | null>(null);

  // On native, overlay the Capacitor build number onto the web package
  // version. e.g. "1.0.0 (6)" instead of just "1.0.0".
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let cancelled = false;
    CapacitorApp.getInfo()
      .then((info) => {
        if (cancelled) return;
        setNativeVersion(`${info.version} (${info.build})`);
      })
      .catch(() => {
        // Native plugin unavailable — fall back to the web version string.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const versionLabel = useMemo(() => {
    if (nativeVersion) return nativeVersion;
    // Vite's `define` inlines the bare identifier at build time, so we read
    // it directly rather than from globalThis (see vite.config.ts).
    return __APP_VERSION__ || t('help.versionFallback');
  }, [nativeVersion, t]);

  const grouped = useMemo(
    () => groupArticlesByCategory(helpArticles, categories),
    [],
  );

  // Terms / Privacy — open the system browser directly. On native,
  // WKWebView / Android WebView route `window.open(..., '_blank')` to the
  // OS browser, matching the behavior the user expects from tapping a link
  // in any native app.
  const openExternal = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <div className="pro-account max-w-3xl mx-auto px-4 pt-4 pb-8">
        {/* Browse topics — one group per category, category name as the section label. */}
        {grouped.length === 0 ? (
          <div className="pro-account__group">
            <p className="pro-account__group-label">{t('help.browseTopics')}</p>
            <ProCard>
              <ProListRow title={t('help.noArticles')} />
            </ProCard>
          </div>
        ) : (
          grouped.map(({ category, articles }) => (
            <div key={category.id} className="pro-account__group">
              <p className="pro-account__group-label">{category.name}</p>
              <ProCard>
                {articles.map((article) => (
                  <ProListRow
                    key={article.slug}
                    title={article.title}
                    note={article.description}
                    onClick={() => navigate(`/pro/help/${article.slug}`)}
                  />
                ))}
              </ProCard>
            </div>
          ))
        )}

        {/* Contact — opens the Feedback form modal (authed mode) and a mailto row. */}
        <div className="pro-account__group">
          <p className="pro-account__group-label">{t('help.contact')}</p>
          <ProCard>
            <ProListRow
              icon={chatbubbleEllipsesOutline}
              title={t('help.contactSupport')}
              note={t('help.contactSupportNote')}
              onClick={() => navigate('/pro/contact-support')}
            />
            <ProListRow
              icon={mailOutline}
              title={t('help.emailSupport')}
              note={t('help.emailSupportNote')}
              onClick={() => {
                window.location.href = `mailto:${SUPPORT_EMAIL}`;
              }}
            />
          </ProCard>
        </div>

        {/* Legal — open the system browser directly (no confirmation sheet). */}
        <div className="pro-account__group">
          <p className="pro-account__group-label">{t('help.legal')}</p>
          <ProCard>
            <ProListRow
              icon={documentTextOutline}
              title={t('help.termsOfUse')}
              onClick={() => openExternal(TERMS_URL)}
            />
            <ProListRow
              icon={shieldCheckmarkOutline}
              title={t('help.privacyPolicy')}
              onClick={() => openExternal(PRIVACY_URL)}
            />
          </ProCard>
        </div>

        {/* About — non-affiliation disclaimer, data sources, and read-only version row. */}
        <div className="pro-account__group">
          <p className="pro-account__group-label">{t('help.about')}</p>
          <ProCard>
            <ProListRow
              icon={informationCircleOutline}
              title={t('help.disclaimerTitle')}
              note={t('help.disclaimer')}
            />
            <ProListRow
              icon={linkOutline}
              title={t('help.dataSources')}
              note={t('help.dataSourcesNote')}
              onClick={() => navigate('/pro/help/data-sources')}
            />
            <ProListRow
              icon={informationCircleOutline}
              title={t('help.appVersion')}
              note={versionLabel}
            />
          </ProCard>
        </div>
      </div>

    </>
  );
};

export default ProHelpPage;
